import { useEffect } from "react";

let staticTagsCleaned = false;

// Removes the static SEO tags shipped in index.html (marked data-rh) once React mounts,
// so per-page tags rendered below are the only ones Google sees.
export function useRemoveStaticSeoTags() {
  useEffect(() => {
    if (staticTagsCleaned) return;
    document.head.querySelectorAll("[data-rh]").forEach((el) => el.remove());
    staticTagsCleaned = true;
  }, []);
}

export default function SEO({
  title,
  description,
  image,
  url,
  type = "website",
  publishedTime,
  modifiedTime,
  author = "Farmwatch",
  section,
  tags = [],
  noindex = false
}) {
  useRemoveStaticSeoTags();

  useEffect(() => {
    const staticRobots = document.head.querySelector('meta[name="robots"]:not([data-seo])');
    if (!staticRobots || !noindex) return;
    const prev = staticRobots.getAttribute("content") || "";
    staticRobots.setAttribute("content", "noindex, nofollow");
    return () => staticRobots.setAttribute("content", prev);
  }, [noindex]);

  const siteTitle = "Farmwatch | Agricultural Intelligence";
  const fullTitle = title ? `${title} | Farmwatch` : siteTitle;
  const defaultDescription = "Your trusted source for live livestock mart prices, agricultural news, commodity markets, and farming intelligence from Ireland, UK, USA and worldwide.";
  const defaultImage = "https://www.farmwatch.com/og-image.png";
  const siteUrl = "https://www.farmwatch.com";

  const finalDescription = description || defaultDescription;
  const finalImage = image || defaultImage;
  const finalUrl = url ? `${siteUrl}${url}` : siteUrl;

  const articleStructuredData = type === "article" ? {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": title,
    "description": finalDescription,
    "image": finalImage,
    "datePublished": publishedTime,
    "dateModified": modifiedTime || publishedTime,
    "author": { "@type": "Organization", "name": author },
    "publisher": {
      "@type": "Organization",
      "name": "Farmwatch",
      "logo": { "@type": "ImageObject", "url": "https://www.farmwatch.com/farmwatch-logo.svg" }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": finalUrl },
    "articleSection": section,
    "keywords": tags.join(", ")
  } : null;

  return (
    <>
      {/* React 19 hoists title/meta/link into <head> automatically */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={finalDescription} />
      {noindex && <meta name="robots" content="noindex, nofollow" data-seo="1" />}

      <meta property="og:type" content={type === "article" ? "article" : "website"} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:site_name" content="Farmwatch" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && section && (
        <meta property="article:section" content={section} />
      )}
      {tags.length > 0 && tags.map((tag, index) => (
        <meta property="article:tag" content={tag} key={index} />
      ))}

      <link rel="canonical" href={finalUrl} />

      {articleStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
        />
      )}
    </>
  );
}

export function MarketSEO({
  title,
  description,
  url,
  region,
  commodities = []
}) {
  useRemoveStaticSeoTags();

  const siteUrl = "https://www.farmwatch.com";
  const fullTitle = `${title} | Farmwatch`;
  const finalUrl = `${siteUrl}${url}`;

  const priceStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": finalUrl,
    "mainEntity": {
      "@type": "ItemList",
      "name": `${region} Livestock Prices`,
      "description": `Live livestock and agricultural commodity prices for ${region}`,
      "numberOfItems": commodities.length,
      "itemListElement": commodities.slice(0, 10).map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": item.name,
          "category": "Agricultural Commodity",
          "offers": {
            "@type": "Offer",
            "price": item.price,
            "priceCurrency": item.currency || "EUR",
            "priceValidUntil": new Date(Date.now() + 86400000).toISOString().split('T')[0]
          }
        }
      }))
    }
  };

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      <link rel="canonical" href={finalUrl} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(priceStructuredData) }}
      />
    </>
  );
}
